import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm, true, event);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} animate - Whether to animate the bubble.
   * @param {Object} event - The original event object.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true, event = null) => {
    // Calculate the new total cart count
    let newCartCount;
    
    // For product form additions, fetch the current cart to get accurate count
    if (comingFromProductForm) {
      // Don't update immediately, wait for server response to avoid flickering
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.currentCartCount = cart.item_count;
          this.refs.cartBubbleCount.classList.toggle('hidden', cart.item_count === 0);
          this.refs.cartBubble.classList.toggle('visually-hidden', cart.item_count === 0);
          this.refs.cartBubble.classList.toggle('cart-bubble--animating', cart.item_count > 0 && animate);
          this.classList.toggle('header-actions__cart-icon--has-cart', cart.item_count > 0);
          
          sessionStorage.setItem(
            'cart-count',
            JSON.stringify({
              value: String(cart.item_count),
              timestamp: Date.now(),
            })
          );
        })
        .catch((error) => {
          console.error('Failed to fetch cart:', error);
          // If fetch fails, use estimated count
          newCartCount = this.currentCartCount + itemCount;
          this.currentCartCount = newCartCount;
        });
      
      // Return early to skip the normal update process
      return;
    } else {
      newCartCount = itemCount;
    }

    // Update the cart count
    this.currentCartCount = newCartCount;

    // Update visibility and animation classes based on the new total count
    this.refs.cartBubbleCount.classList.toggle('hidden', newCartCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', newCartCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', newCartCount > 0 && animate);

    this.classList.toggle('header-actions__cart-icon--has-cart', newCartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(newCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
